import React, { useState, useEffect, useContext } from 'react';
import api from '../../../services/api';
import DocumentsTab from '../../../components/Employee/DocumentsTab';
import { AuthContext } from '../../../context/AuthContext';

const EmployeeForm = ({ employee, initialTab = 'details', onClose }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    employeeId: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'Employee',
    department: '',
    designation: '',
    workLocation: 'OFFICE',
    dateOfBirth: '',
    dateOfJoining: '',
    isActive: true,
    basicPay: '',
    hra: '',
    managerId: '',
    customWorkingHours: ''
  });
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab); // 'details' or 'documents'

  const isEdit = !!employee;

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [depRes, empRes, rolesRes] = await Promise.all([
          api.get('/departments'),
          api.get('/employees'),
          api.get('/custom-roles')
        ]);
        setDepartments(depRes.data);
        setManagers(empRes.data);
        setCustomRoles(rolesRes.data);
      } catch (err) {
        console.error('Failed to fetch dropdown data');
      }
    };
    fetchDropdowns();

    if (employee) {
      setFormData({
        employeeId: employee.employeeId || '',
        email: employee.email || '',
        password: '', // don't populate password
        firstName: employee.firstName || '',
        lastName: employee.lastName || '',
        role: employee.role || 'Employee',
        department: employee.department?._id || '',
        designation: employee.designation || '',
        workLocation: employee.workLocation || 'OFFICE',
        dateOfBirth: employee.dateOfBirth ? new Date(employee.dateOfBirth).toISOString().split('T')[0] : '',
        dateOfJoining: employee.dateOfJoining ? new Date(employee.dateOfJoining).toISOString().split('T')[0] : '',
        isActive: employee.isActive,
        basicPay: '', // In edit mode, salary is handled in the SalaryTab
        hra: '',
        managerId: employee.managerId?._id || employee.managerId || '',
        customWorkingHours: employee.customWorkingHours || ''
      });
    }
  }, [employee]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSubmit = { ...formData };
      if (!dataToSubmit.managerId) {
        delete dataToSubmit.managerId;
      }
      if (!dataToSubmit.department) {
        delete dataToSubmit.department;
      }
      
      if (dataToSubmit.customWorkingHours) {
        dataToSubmit.customWorkingHours = parseFloat(dataToSubmit.customWorkingHours);
      } else {
        dataToSubmit.customWorkingHours = null;
      }
      if (isEdit && !dataToSubmit.password) {
        delete dataToSubmit.password; // Don't send empty password on edit
      }

      if (isEdit) {
        await api.put(`/employees/${employee._id}`, dataToSubmit);
      } else {
        const res = await api.post('/employees', dataToSubmit);
        // Set initial salary structure
        try {
          const newEmpId = res.data._id || res.data.data?._id;
          if (newEmpId && (formData.basicPay > 0 || formData.hra > 0)) {
            const defaultDate = new Date();
            defaultDate.setDate(1); // Set to 1st of current month
            await api.post(`/salary/${newEmpId}`, {
              basic: formData.basicPay * 100,
              hra: formData.hra * 100,
              allowances: [],
              effectiveFrom: defaultDate.toISOString().split('T')[0],
              changeReason: 'Initial setup during creation'
            });
          }
        } catch (salaryErr) {
          console.error('Failed to set initial salary:', salaryErr);
          // Don't fail the whole employee creation if salary fails, just log it
        }
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 py-10">
      <div className="relative mx-auto p-8 border w-full max-w-3xl shadow-lg rounded-xl bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Employee' : 'Add New Employee'}</h3>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        {isEdit && (
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-2 px-4 font-medium text-sm border-b-2 outline-none transition-colors ${
                activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Profile Info
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`py-2 px-4 font-medium text-sm border-b-2 outline-none transition-colors ${
                activeTab === 'documents' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Documents
            </button>
          </div>
        )}

        {error && <div className="mb-4 bg-red-50 text-red-700 p-3 rounded">{error}</div>}

        {activeTab === 'details' && (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                <input type="text" name="employeeId" autoComplete="off" value={formData.employeeId} placeholder="Auto-generated (e.g. CNB001) if blank" onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select name="role" value={formData.role} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500">
                  <option value="Employee">Employee</option>
                  <option value="HR">HR</option>
                  <option value="Intern">Intern</option>
                  {user?.role === 'Super Admin' && (
                    <>
                      <option value="Admin">Admin</option>
                      <option value="Super Admin">Super Admin</option>
                    </>
                  )}
                  {customRoles.map(r => <option key={r._id} value={r.name}>{r.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input type="text" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="email" autoComplete="off" required value={formData.email} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password {isEdit && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}</label>
                <input type="password" name="password" autoComplete="new-password" required={!isEdit} value={formData.password} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <select name="department" value={formData.department} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select Department</option>
                  {departments.map(d => (
                    <option key={d._id} value={d._id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input type="text" name="designation" value={formData.designation} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Location</label>
                <select name="workLocation" value={formData.workLocation} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500">
                  <option value="OFFICE">Office</option>
                  <option value="WFH">WFH (Remote)</option>
                  <option value="HYBRID">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Working Hrs / Day</label>
                <input type="number" step="0.5" name="customWorkingHours" value={formData.customWorkingHours} onChange={handleChange} placeholder="Default (e.g. 9)" className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Leave blank to use global setting.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reports To (Manager)</label>
                <select name="managerId" value={formData.managerId} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500">
                  <option value="">No Manager (Top Level)</option>
                  {managers.filter(m => m._id !== employee?._id).map(m => (
                    <option key={m._id} value={m._id}>{m.firstName} {m.lastName} ({m.employeeId})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Joining</label>
                <input type="date" name="dateOfJoining" value={formData.dateOfJoining} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
              </div>
              
              {!isEdit && (
                <>
                  <div className="col-span-2 border-t mt-4 pt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Initial Salary Structure</h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Basic Pay (Monthly ₹)</label>
                    <input type="number" name="basicPay" min="0" value={formData.basicPay} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HRA (Monthly ₹)</label>
                    <input type="number" name="hra" min="0" value={formData.hra} onChange={handleChange} className="w-full p-2 border rounded focus:ring-blue-500 focus:border-blue-500" />
                  </div>
                </>
              )}
              
              {isEdit && (
                <div className="col-span-2 flex items-center mt-2">
                  <input type="checkbox" id="isActive" name="isActive" checked={formData.isActive} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">Active Account</label>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button type="button" onClick={() => onClose(false)} className="mr-3 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                {loading ? 'Saving...' : 'Save Employee'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'documents' && employee && (
          <div className="mt-2">
            <DocumentsTab employeeId={employee._id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeForm;
